"use client";
import { useQuery, useQueries } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { DrawerProvider, Loader, ToastProvider } from "../design";
import {
    getAllTranslationsQuery,
    getCancellationOffersShortDescriptionQuery,
    getTranslationsByTypesQuery,
} from "../queries/translations.query";
import { APIFunction } from "../utils/apiFunction";
import { BYOB_BUNDLE_EVENTS, EVENT_TYPES } from "../utils/constants";
import { emitCustomEvent, handleError } from "../utils/helpers";
import Translate from "../utils/translations";
import { LoopAuthProvider } from "./auth-provider";
import { LoopProvider } from "./loop-provider";
import { LoyaltyProvider } from "./loyalty-provider";
import { QuickActionsProvider } from "./quick-actions-provider";
import { CustomerPortalContext } from "../backend/serverProps";
import { tryCatch } from "../utils/try-catch";

interface AppProviderProps {
    children: React.ReactNode;
    shopifyDomain: string;
    locale: string | null;
    sessionToken: string | null;
    previewThemeId: string | null;
    customerPortalContext?: CustomerPortalContext;
}

export default function AppProvider({
    children,
    shopifyDomain,
    locale,
    sessionToken,
    previewThemeId,
    customerPortalContext
}: AppProviderProps) {

    const router = useRouter();
    const [isCustomizationFilesLoaded, setIsCustomizationFilesLoaded] = useState(false);
    const [cssLink, setCssLink] = useState(null);
    const [customCssLink, setCustomCssLink] = useState(null);
    const [themeNanoId, setThemeNanoId] = useState(null);

    const previewThemeMeta = customerPortalContext?.themeMetadata ?? null;

    async function loadCustomizationFiles(shopifyDomain: string) {

        let themeNanoId = customerPortalContext?.themeNanoId;
        if (!themeNanoId) {
            const { data, error } = await tryCatch(APIFunction.getActiveTheme(shopifyDomain));
            if (error) {
                handleError(error, "app-provider.tsx", "loadCustomizationFiles", `shopifyDomain ${shopifyDomain}`);
            }
            themeNanoId = router?.query?.previewThemeId as string || data.data.nanoId || null;
        }
        setThemeNanoId(themeNanoId);

        let isPreview = customerPortalContext?.isPreviewTheme;

        if (!themeNanoId) {
            return;
        }

        const cdnDomain = `${process.env.NEXT_PUBLIC_BRANDING_CDN_DOMAIN}/customerportal`;
        const timestamp = (Date.now() / 1000).toFixed(0);

        const domainSlug = shopifyDomain.replace(".myshopify.com", "");
        const standardCssFileUrl = `${cdnDomain}/${domainSlug}/${themeNanoId}/standard.css${isPreview ? `?timestamp=${timestamp}` : ""}`;
        const customCssFileUrl = `${cdnDomain}/${domainSlug}/${themeNanoId}/custom.css${isPreview ? `?timestamp=${timestamp}` : ""}`;
        const customJsFileUrl = `${cdnDomain}/${domainSlug}/${themeNanoId}/custom.js${isPreview ? `?timestamp=${timestamp}` : ""}`;

        setCssLink(standardCssFileUrl);
        setCustomCssLink(customCssFileUrl);

        const script = document.createElement("script");
        script.defer = true;
        script.src = `${customJsFileUrl}`;
        script.type = "text/javascript";
        document.body.appendChild(script);
    }

    function loadConfettiScript() {
        if (router.pathname.includes("/gift")) {
            const script = document.createElement("script");
            script.defer = true;
            script.src = `https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js`;
            script.type = "text/javascript";
            document.body.appendChild(script);
        }
    }

    function renderCssLinks() {
        return (
            <>
                {cssLink && <link rel="stylesheet" href={cssLink} />}
                {customCssLink && <link rel="stylesheet" href={customCssLink} />}
            </>
        );
    }

    function getPreviewBanner() {
        if (previewThemeId && previewThemeMeta && customerPortalContext?.isPreviewTheme) {
            return (
                <div className="loop-preview-banner">
                    You're currently {router?.query?.isPublished == "1" ? "viewing" : "previewing"} customer portal{" "}
                    <strong>{previewThemeMeta.name}</strong> theme.
                    <br />
                    This is a live subscription and any action taken will modify the actual data in production.
                </div>
            );
        }
    }

    useEffect(() => {
        if (router.isReady) {
            loadConfettiScript();
        }
    }, [router.isReady]);

    useEffect(() => {
        if (isCustomizationFilesLoaded) {
            setTimeout(() => {
                const urlParams = new URLSearchParams(window.location.search);
                const event = urlParams.get("event");

                if (event === BYOB_BUNDLE_EVENTS.BUNDLE_ADD_AS_ONE_TIME_EVENT) {
                    emitCustomEvent(EVENT_TYPES.BUNDLE_ADD_AS_ONE_TIME);
                } else if (event === BYOB_BUNDLE_EVENTS.BUNDLE_ADD_AS_SUBSCRIPTION_EVENT) {
                    emitCustomEvent(EVENT_TYPES.BUNDLE_ADD_AS_SUBSCRIPTION);
                } else if (event === BYOB_BUNDLE_EVENTS.BUNDLE_EDITED) {
                    emitCustomEvent(EVENT_TYPES.BUNDLE_VARIANT_CHANGE);
                }
            }, 800);
        } else {
            if (shopifyDomain) {
                loadCustomizationFiles(shopifyDomain);
                setIsCustomizationFilesLoaded(true);
            }
        }
    }, [isCustomizationFilesLoaded, shopifyDomain]);

    if (!themeNanoId || !isCustomizationFilesLoaded) {
        return (
            <div className="loop-background" id="loop-background">
                <Loader />
            </div>
        );
    }

    return (
        <>
            <TranslationsWrapper shopifyDomain={shopifyDomain} locale={locale} previewThemeId={themeNanoId}>
                <LoopProviders customerPortalContext={customerPortalContext} themeNanoId={themeNanoId}>
                    {children}
                </LoopProviders>
            </TranslationsWrapper>
            {renderCssLinks()}
            {getPreviewBanner()}
        </>
    );
}

function LoopProviders({
    children,
    customerPortalContext,
    themeNanoId
}: {
    children: any;
    customerPortalContext?: CustomerPortalContext;
    themeNanoId: string;
}) {
    return (
        <LoopAuthProvider customerPortalContext={customerPortalContext}>
            <LoopProvider customerPortalContext={customerPortalContext} themeNanoId={themeNanoId}>
                <LoyaltyProvider>
                    <ToastProvider>
                        <DrawerProvider>
                            <QuickActionsProvider>
                                <div
                                    className="loop-background"
                                    id="loop-background"
                                    style={{ minHeight: "400px", transition: "height 0.3s ease-in-out" }}
                                >
                                    <div className="loop-container loop-bs-container p-0">{children}</div>
                                </div>
                            </QuickActionsProvider>
                        </DrawerProvider>
                    </ToastProvider>
                </LoyaltyProvider>
            </LoopProvider>
        </LoopAuthProvider>
    );
}

function TranslationsWrapper({
    children,
    shopifyDomain,
    locale,
    previewThemeId
}: {
    children: React.ReactNode;
    shopifyDomain: string;
    locale: string;
    previewThemeId: string;
}) {
    return (
        <TranslationsLoader shopifyDomain={shopifyDomain} locale={locale} previewThemeId={previewThemeId}>
            {children}
        </TranslationsLoader>
    );
}

function TranslationsLoader(props: {
    children: React.ReactNode;
    shopifyDomain: string;
    locale: string;
    previewThemeId: string;
}) {
    const queries = useQueries({
        queries: [
            getAllTranslationsQuery(props.shopifyDomain, props.previewThemeId, props.locale),
            getTranslationsByTypesQuery(
                props.shopifyDomain,
                props.locale,
                "CP_CANCELLATION_FLOW_BENEFITS,CP_CANCELLATION_FLOW_MIN_ORDERS,CP_CANCELLATION_FLOW_INSTALLMENT_MIN_ORDERS,CP_CANCELLATION_FLOW_INSTRUCTIONS",
            ),
            getCancellationOffersShortDescriptionQuery(props.shopifyDomain, props.locale),
        ],
    });

    const [allTranslationsResponse, translationsByTypeResponse, cancellationOffersShortDescriptionResponse] = queries;

    // Show loader while any query is still loading
    if (queries.some(query => query.isLoading)) {
        return (
            <div className="loop-background" id="loop-background">
                <Loader />
            </div>
        );
    }

    // Set translations only when data is available
    if (allTranslationsResponse.data?.data) {
        Translate.set(allTranslationsResponse.data.data.translations);
        Translate.set(allTranslationsResponse.data.data.entities);
    }
    if (translationsByTypeResponse.data?.data) {
        for (const value of Object.values(translationsByTypeResponse.data.data)) {
            Translate.set(value);
        }
    }
    if (cancellationOffersShortDescriptionResponse.data?.data) {
        Translate.set(cancellationOffersShortDescriptionResponse.data.data);
    }

    return <>{props.children}</>;
}
